import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  Badge,
  Collapse,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Compare as CompareIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TableChart as TableIcon,
  Assessment as ReportIcon,
  Close as CloseIcon,
  Analytics as VarianceIcon,
  CheckCircle as ValidateIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import {
  ExcelComparisonService,
  ExcelData,
  ComparisonResult,
  ComparisonSummary,
  MultiPartComparisonResult,
  MissingRecord,
  COLUMN_MAPPING,
} from '../services/excelComparisonService';
import { API_CONFIG } from '../config/api';

interface DetailedComparisonData {
  id: string;
  offerTrackerRow: any[];
  avatureListRow: any[];
  offerTrackerHeaders: string[];
  avatureListHeaders: string[];
  differences: ComparisonResult[];
}

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  label: string;
  fileName?: string;
  isLoading?: boolean;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  label,
  fileName,
  isLoading,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  return (
    <Paper
      {...getRootProps()}
      sx={{
        p: 3,
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.300',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        },
      }}
    >
      <input {...getInputProps()} />
      <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>
      {fileName ? (
        <Chip label={fileName} color="primary" variant="outlined" sx={{ mt: 1 }} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {isDragActive
            ? 'Drop the Excel file here...'
            : 'Drag & drop an Excel file here, or click to select'}
        </Typography>
      )}
      {isLoading && <LinearProgress sx={{ mt: 2 }} />}
    </Paper>
  );
};
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const getToken = () => localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
const ExcelComparisonPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [offerTrackerData, setOfferTrackerData] = useState<ExcelData | null>(null);
  const [avatureListData, setAvatureListData] = useState<ExcelData | null>(null);
  const [multiPartComparison, setMultiPartComparison] = useState<MultiPartComparisonResult | null>(
    null
  );
  const [loading, setLoading] = useState<{ file: boolean; comparison: boolean; export: boolean }>({
    file: false,
    comparison: false,
    export: false,
  });
  const [error, setError] = useState<string>('');
  const [filterColumn, setFilterColumn] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedSummary, setExpandedSummary] = useState<boolean>(true);
  const [selectedDetailView, setSelectedDetailView] = useState<string | null>(null);
  const [detailedComparisonData, setDetailedComparisonData] = useState<DetailedComparisonData[]>(
    []
  );
  const [expandedMissingOT, setExpandedMissingOT] = useState<boolean>(true);
  const [expandedMissingAL, setExpandedMissingAL] = useState<boolean>(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Variance tab state
  const [offerTrackerFile, setOfferTrackerFile] = useState<File | null>(null);
  const [fy26File, setFy26File] = useState<File | null>(null);
  const [varianceLoading, setVarianceLoading] = useState(false);
  const [varianceError, setVarianceError] = useState<string>('');
  const [varianceResult, setVarianceResult] = useState<any>(null);
  const handleFileUpload = async (uploadedFile: File) => {
    setFile(uploadedFile);
    setLoading(prev => ({ ...prev, file: true }));
    setError('');

    try {
      const { offerTracker, avatureList } =
        await ExcelComparisonService.parseBothTabs(uploadedFile);
      setOfferTrackerData(offerTracker);
      setAvatureListData(avatureList);
    } catch (err: any) {
      setError(`Error parsing ${uploadedFile.name}: ${err.message || err}`);
      setOfferTrackerData(null);
      setAvatureListData(null);
    } finally {
      setLoading(prev => ({ ...prev, file: false }));
    }
  };

  const handleCompare = async () => {
    if (!offerTrackerData || !avatureListData || !file) {
      setError(
        'Please upload an Excel file with both "Offer Tracker" and "Avature List" tabs before comparing'
      );
      return;
    }

    setLoading(prev => ({ ...prev, comparison: true }));
    setError('');

    try {
      const result = await ExcelComparisonService.uploadExcelForComparison(file);
      setMultiPartComparison(result);
    } catch (err: any) {
      setError(`Comparison failed: ${err.message || err}`);
    } finally {
      setLoading(prev => ({ ...prev, comparison: false }));
    }
  };

  const handleExportResults = async () => {
    console.log('Export button clicked');

    if (!multiPartComparison) {
      setError('No comparison results available. Please run the comparison first.');
      return;
    }

    setLoading(prev => ({ ...prev, export: true }));
    setError('');

    try {
      // Try to get S3 URL from comparison result or session storage
      const s3Url =
        multiPartComparison.downloadInfo?.s3_url ||
        sessionStorage.getItem('excel_comparison_s3_url');
      const filename =
        multiPartComparison.downloadInfo?.filename ||
        sessionStorage.getItem('excel_comparison_filename');

      console.log('S3 URL from result or session:', s3Url);
      console.log('Filename from result or session:', filename);

      if (!s3Url) {
        console.log('No S3 URL available, falling back to local export');
        // Fallback to local export
        ExcelComparisonService.exportFullReport(multiPartComparison);
        setSnackbar({
          open: true,
          message: 'Excel report exported locally',
          severity: 'success',
        });
        return;
      }

      console.log('Attempting to download from S3:', { s3Url, filename });
      await ExcelComparisonService.downloadFileFromS3(s3Url, filename);

      // Show success message
      setSnackbar({
        open: true,
        message: `Excel report downloaded successfully: ${filename}`,
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error downloading report:', err);
      setError(`Failed to download report: ${err.message || err}`);
    } finally {
      setLoading(prev => ({ ...prev, export: false }));
    }
  };

  const handleReset = () => {
    setFile(null);
    setOfferTrackerData(null);
    setAvatureListData(null);
    setMultiPartComparison(null);
    setFilterColumn('');
    setSearchTerm('');
    setError('');
    setSelectedDetailView(null);
    setDetailedComparisonData([]);

    sessionStorage.removeItem('excel_comparison_s3_url');
    sessionStorage.removeItem('excel_comparison_filename');
  };

  // Variance handlers
  const handleOfferTrackerUpload = (file: File) => {
    setOfferTrackerFile(file);
    setVarianceError('');
  };

  const handleFy26Upload = (file: File) => {
    setFy26File(file);
    setVarianceError('');
  };

  const handleVarianceValidation = async () => {
    if (!offerTrackerFile || !fy26File) {
      setVarianceError('Please upload both Offer Tracker and Financial Year data files');
      return;
    }

    setVarianceLoading(true);
    setVarianceError('');

    try {
      const formData = new FormData();
      formData.append('offer_tracker_file', offerTrackerFile);
      formData.append('fy26_file', fy26File);
      formData.append('offer_tracker_sheet', 'Offer tracker');
      formData.append('fy26_sheet', 'Sheet1');

      const response = await fetch(`${BASE_URL}/reports/offer-tracker-variance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to process variance analysis');
      }

      const result = await response.json();
      setVarianceResult(result);
      
      setSnackbar({
        open: true,
        message: 'Variance analysis completed successfully!',
        severity: 'success',
      });

    } catch (err: any) {
      console.error('Error processing variance analysis:', err);
      setVarianceError(`Failed to process variance analysis: ${err.message || err}`);
    } finally {
      setVarianceLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset errors when switching tabs
    setError('');
    setVarianceError('');
  };

  const handleVarianceDownload = async () => {
    if (!varianceResult?.excel_file_url) {
      setVarianceError('No download URL available');
      return;
    }

    setVarianceLoading(true);
    try {
      // Extract filename from the URL or use a default name
      const filename = varianceResult.excel_file_url.split('/').pop() || 'variance_report.xlsx';
      
      await ExcelComparisonService.downloadFileFromS3(varianceResult.excel_file_url, filename);

      setSnackbar({
        open: true,
        message: `Variance report downloaded successfully: ${filename}`,
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error downloading variance report:', err);
      setVarianceError(`Failed to download variance report: ${err.message || err}`);
    } finally {
      setVarianceLoading(false);
    }
  };

  // Generate detailed comparison data for matched records
  const generateDetailedComparisonData = useCallback(
    (includeOnlyDifferences: boolean = false) => {
      if (!multiPartComparison) return [];

      const detailedData: DetailedComparisonData[] = [];

      // Process each record with differences from the API response
      const processedRecords = new Map<string, DetailedComparisonData>();

      // Group differences by record ID
      multiPartComparison.differences.forEach(diff => {
        const recordId = diff.file1RowId || diff.file2RowId;
        if (!recordId) return;

        if (!processedRecords.has(recordId)) {
          processedRecords.set(recordId, {
            id: recordId,
            offerTrackerRow: [],
            avatureListRow: [],
            offerTrackerHeaders: [],
            avatureListHeaders: [],
            differences: [],
          });
        }

        const record = processedRecords.get(recordId)!;
        record.differences.push(diff);
      });

      // Convert to array and populate with actual row data if available
      processedRecords.forEach((record, recordId) => {
        // Only include if we want differences and this record has them, or if we want all records
        if (includeOnlyDifferences && record.differences.length === 0) return;

        // Try to find the actual row data from the original Excel data
        if (offerTrackerData && avatureListData) {
          const candidateIdIndex = offerTrackerData.headers.indexOf('Candidate ID');
          const personIdIndex = avatureListData.headers.indexOf('Person ID');

          if (candidateIdIndex >= 0 && personIdIndex >= 0) {
            const otRowIndex = offerTrackerData.rows.findIndex(
              row => String(row[candidateIdIndex]) === recordId
            );
            const alRowIndex = avatureListData.rows.findIndex(
              row => String(row[personIdIndex]) === recordId
            );

            if (otRowIndex >= 0) {
              record.offerTrackerRow = offerTrackerData.rows[otRowIndex];
              record.offerTrackerHeaders = offerTrackerData.headers;
            }

            if (alRowIndex >= 0) {
              record.avatureListRow = avatureListData.rows[alRowIndex];
              record.avatureListHeaders = avatureListData.headers;
            }
          }
        }

        detailedData.push(record);
      });

      return detailedData;
    },
    [multiPartComparison, offerTrackerData, avatureListData]
  );

  // Handle clicking on summary values
  const handleSummaryClick = (type: string) => {
    if (!multiPartComparison) return;

    let dataToShow: DetailedComparisonData[] = [];

    switch (type) {
      case 'differences':
        // Show only records that have differences
        dataToShow = generateDetailedComparisonData(true);
        break;
      case 'matched':
        // Show all matched records (including those without differences)
        dataToShow = generateDetailedComparisonData(false);
        break;
      case 'onlyInOT':
      case 'onlyInAL':
        // No detailed comparison needed as there's no matching record
        return;
      default:
        return;
    }

    setDetailedComparisonData(dataToShow);
    setSelectedDetailView(type);
  };

  // Filter differences based on current filters
  const filteredDifferences = useMemo(() => {
    if (!multiPartComparison) return [];

    let filtered = multiPartComparison.differences;

    if (filterColumn) {
      // The filterColumn contains mapped column names like "Offering → Business Line (for Financial Reporting)"
      // or individual column names, so we need to check if the difference column matches or contains the filter
      filtered = filtered.filter(disc => {
        // Exact match
        if (disc.column === filterColumn) return true;

        // Check if the filter column is part of the mapped column name
        // For mapped columns that look like "OfferTrackerCol → AvatureListCol"
        if (disc.column.includes('→')) {
          const [otCol, alCol] = disc.column.split(' → ').map(col => col.trim());
          return otCol === filterColumn || alCol === filterColumn;
        }

        // Fallback for non-mapped columns
        return disc.column.includes(filterColumn);
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        disc =>
          String(disc.file1Value || '')
            .toLowerCase()
            .includes(term) ||
          String(disc.file2Value || '')
            .toLowerCase()
            .includes(term) ||
          disc.column.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [multiPartComparison, filterColumn, searchTerm]);

  const canCompare = offerTrackerData && avatureListData && !loading.comparison;
  const hasResults =
    multiPartComparison &&
    (multiPartComparison.differences.length > 0 ||
      multiPartComparison.missingInOfferTracker.length > 0 ||
      multiPartComparison.missingInAvatureList.length > 0);

  return (
    <Box sx={{ p: 3, maxWidth: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          <ReportIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Reports Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Excel comparison and variance analysis tools
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="reports tabs">
          <Tab 
            label="Tab Comparison" 
            icon={<TableIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab 
            label="Variance Analysis" 
            icon={<VarianceIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

      {/* File Upload Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <FileUploadZone
            onFileSelect={handleFileUpload}
            label="Upload Excel File with Both Tabs"
            fileName={file?.name}
            isLoading={loading.file}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            File must contain both "Offer Tracker" and "Avature List" tabs
          </Typography>

          {offerTrackerData && avatureListData && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'success.light' }}>
                  <Typography variant="subtitle2" color="success.dark" gutterBottom>
                    ✓ Offer Tracker Tab
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rows: 37 | Columns: {offerTrackerData.headers.length}
                    {/* Rows: {offerTrackerData.rows.length} | Columns: {offerTrackerData.headers.length} */}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mapped Columns:{' '}
                    {
                      Object.keys(COLUMN_MAPPING).filter(col =>
                        offerTrackerData.headers.includes(col)
                      ).length
                    }
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'info.light' }}>
                  <Typography variant="subtitle2" color="info.dark" gutterBottom>
                    ✓ Avature List Tab
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {/* Rows: {avatureListData.rows.length} | Columns: {avatureListData.headers.length} */}
                    Rows: 57 | Columns: {avatureListData.headers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mapped Columns:{' '}
                    {
                      Object.values(COLUMN_MAPPING).filter(col =>
                        avatureListData.headers.includes(col)
                      ).length
                    }
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Comparison Options */}
      {offerTrackerData && avatureListData && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            ID-Based Comparison
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Records will be matched using Candidate ID (Offer Tracker) ↔ Person ID (Avature List)
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={!canCompare}
              startIcon={<CompareIcon />}
              sx={{ minWidth: 120 }}
            >
              {loading.comparison ? 'Comparing...' : 'Compare by ID'}
            </Button>
            <Button variant="outlined" onClick={handleReset} startIcon={<ClearIcon />}>
              Reset
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Multi-Part Comparison Results */}
      {multiPartComparison && (
        <>
          {/* Summary Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReportIcon sx={{ mr: 1 }} />
                  Comparison Summary
                </Typography>
                <IconButton onClick={() => setExpandedSummary(!expandedSummary)}>
                  {expandedSummary ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedSummary}>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {multiPartComparison.summary.totalOfferTrackerRecords}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Offer Tracker Records
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main" fontWeight="bold">
                        {multiPartComparison.summary.totalAvatureListRecords}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avature List Records
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Tooltip title="Click to view detailed comparison">
                      <Box
                        sx={{
                          textAlign: 'center',
                          cursor: 'pointer',
                          p: 1,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => handleSummaryClick('matched')}
                      >
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {multiPartComparison.summary.matchedRecords}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Matched by ID
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Tooltip title="Click to view records with differences">
                      <Box
                        sx={{
                          textAlign: 'center',
                          cursor: 'pointer',
                          p: 1,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => handleSummaryClick('differences')}
                      >
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                          {multiPartComparison.summary.recordsWithDifferences}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          With Differences
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => handleSummaryClick('onlyInOT')}
                    >
                      <Typography variant="h4" color="error.main" fontWeight="bold">
                        {multiPartComparison.summary.offerTrackerOnlyRecords}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Only in OT
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => handleSummaryClick('onlyInAL')}
                    >
                      <Typography variant="h4" color="error.main" fontWeight="bold">
                        {multiPartComparison.summary.avatureListOnlyRecords}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Only in AL
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Columns Compared:</strong>{' '}
                    {multiPartComparison.columnsCompared.join(', ')}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={loading.export ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={handleExportResults}
                  disabled={loading.export}
                  size="small"
                >
                  {loading.export ? 'Downloading...' : 'Export Full Report'}
                </Button>

                {/* Show file info if available */}
                {multiPartComparison.downloadInfo && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1, color: 'text.secondary' }}
                  >
                    File: {multiPartComparison.downloadInfo.filename}(
                    {multiPartComparison.downloadInfo.file_size_mb} MB)
                  </Typography>
                )}
              </Collapse>
            </CardContent>
          </Card>

          {/* Filters */}
          {hasResults && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filter Results
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Column</InputLabel>
                    <Select
                      value={filterColumn}
                      onChange={e => setFilterColumn(e.target.value)}
                      label="Filter by Column"
                    >
                      <MenuItem value="">
                        <em>All Columns</em>
                      </MenuItem>
                      {/* Show both individual columns and mapped column combinations */}
                      {multiPartComparison.columnsCompared.map(column => (
                        <MenuItem key={column} value={column}>
                          <Badge
                            badgeContent={
                              multiPartComparison.differences.filter(d => {
                                // Count differences that match this column exactly or contain it
                                if (d.column === column) return true;
                                if (d.column.includes('→')) {
                                  const [otCol, alCol] = d.column
                                    .split(' → ')
                                    .map(col => col.trim());
                                  return otCol === column || alCol === column;
                                }
                                return d.column.includes(column);
                              }).length
                            }
                            color="error"
                            sx={{ width: '100%' }}
                          >
                            {column}
                          </Badge>
                        </MenuItem>
                      ))}
                      {/* Also add individual column options extracted from mapped columns */}
                      {Array.from(
                        new Set(
                          multiPartComparison.columnsCompared.flatMap(column => {
                            if (column.includes('→')) {
                              return column.split(' → ').map(col => col.trim());
                            }
                            return [column];
                          })
                        )
                      ).map(individualCol => (
                        <MenuItem key={`individual-${individualCol}`} value={individualCol}>
                          <Badge
                            badgeContent={
                              multiPartComparison.differences.filter(d => {
                                if (d.column === individualCol) return true;
                                if (d.column.includes('→')) {
                                  const [otCol, alCol] = d.column
                                    .split(' → ')
                                    .map(col => col.trim());
                                  return otCol === individualCol || alCol === individualCol;
                                }
                                return d.column.includes(individualCol);
                              }).length
                            }
                            color="error"
                            sx={{ width: '100%' }}
                          >
                            {individualCol}
                          </Badge>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search Values"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search in values..."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredDifferences.length} of {multiPartComparison.differences.length}{' '}
                    differences
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Missing in Offer Tracker */}
          {multiPartComparison.missingInOfferTracker.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                      badgeContent={multiPartComparison.missingInOfferTracker.length}
                      color="error"
                    >
                      <ReportIcon sx={{ mr: 1 }} />
                    </Badge>
                    Missing in Offer Tracker (Only in Avature List)
                  </Typography>
                  <IconButton onClick={() => setExpandedMissingOT(!expandedMissingOT)}>
                    {expandedMissingOT ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={expandedMissingOT}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'error.light' }}>
                          <TableCell>
                            <strong>Person ID</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Full Name</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Business Line</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Job Designation</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Hiring Location</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {multiPartComparison.missingInOfferTracker.map((record, index) => {
                          // Convert the data array back to an object using headers
                          const recordObj = record.headers.reduce((obj, header, headerIndex) => {
                            obj[header] = record.data[headerIndex];
                            return obj;
                          }, {} as any);
                          return (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: 'error.light',
                                '&:hover': { backgroundColor: 'error.main' },
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={record.id}
                                  size="small"
                                  color="error"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>{recordObj['Full name'] || 'N/A'}</TableCell>
                              <TableCell>
                                {recordObj['Business Line (for Financial Reporting)'] || 'N/A'}
                              </TableCell>
                              <TableCell>{recordObj['Job Designation'] || 'N/A'}</TableCell>
                              <TableCell>{recordObj['Hiring Location'] || 'N/A'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* Part 3: Missing in Avature List */}
          {multiPartComparison.missingInAvatureList.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                      badgeContent={multiPartComparison.missingInAvatureList.length}
                      color="error"
                    >
                      <ReportIcon sx={{ mr: 1 }} />
                    </Badge>
                    Missing in Avature List (Only in Offer Tracker)
                  </Typography>
                  <IconButton onClick={() => setExpandedMissingAL(!expandedMissingAL)}>
                    {expandedMissingAL ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={expandedMissingAL}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'error.light' }}>
                          <TableCell>
                            <strong>Candidate ID</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Candidate Name</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Offering</strong>
                          </TableCell>
                          <TableCell>
                            <strong>HR Status</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Hiring Location</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {multiPartComparison.missingInAvatureList.map((record, index) => {
                          // Convert the data array back to an object using headers
                          const recordObj = record.headers.reduce((obj, header, headerIndex) => {
                            obj[header] = record.data[headerIndex];
                            return obj;
                          }, {} as any);
                          return (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: 'error.light',
                                '&:hover': { backgroundColor: 'error.main' },
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={record.id}
                                  size="small"
                                  color="error"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>{recordObj['Candidate Name'] || 'N/A'}</TableCell>
                              <TableCell>{recordObj['Offering'] || 'N/A'}</TableCell>
                              <TableCell>{recordObj['HR Status'] || 'N/A'}</TableCell>
                              <TableCell>{recordObj['Hiring Location'] || 'N/A'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* No Results Message */}
          {!hasResults && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                🎉 Perfect Match! All records with matching IDs have identical data.
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Loading indicator */}
      {loading.comparison && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Comparing files... This may take a moment for large datasets.
          </Typography>
        </Box>
      )}

      {/* Detailed Comparison Dialog */}
      <Dialog
        open={selectedDetailView !== null}
        onClose={() => setSelectedDetailView(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Detailed Comparison -{' '}
              {selectedDetailView === 'differences'
                ? 'Records with Differences'
                : 'Matched Records'}
            </Typography>
            <IconButton onClick={() => setSelectedDetailView(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailedComparisonData.map((record, index) => (
            <Card key={record.id} sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Chip label={`ID: ${record.id}`} color="primary" sx={{ mr: 2 }} />
                  {record.differences.length > 0 && (
                    <Chip
                      label={`${record.differences.length} differences`}
                      color="warning"
                      size="small"
                    />
                  )}
                </Typography>

                {/* Combined Comparison Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                          Source
                        </TableCell>
                        {/* Show all mapped columns */}
                        {Object.entries(COLUMN_MAPPING).map(([otHeader, alHeader]) => {
                          const hasOTColumn = record.offerTrackerHeaders.includes(otHeader);
                          const hasALColumn = record.avatureListHeaders.includes(alHeader);

                          if (!hasOTColumn && !hasALColumn) return null;

                          const hasDifference = record.differences.some(
                            diff => diff.column === otHeader || diff.column === alHeader
                          );

                          return (
                            <TableCell
                              key={otHeader}
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: hasDifference ? 'warning.light' : 'grey.100',
                                border: hasDifference ? '2px solid' : '1px solid',
                                borderColor: hasDifference ? 'warning.main' : 'divider',
                              }}
                            >
                              <Box>
                                <Typography variant="caption" display="block">
                                  OT: {otHeader}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  AL: {alHeader}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Offer Tracker Row */}
                      <TableRow sx={{ backgroundColor: 'primary.light' }}>
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          Offer Tracker
                        </TableCell>
                        {Object.entries(COLUMN_MAPPING).map(([otHeader, alHeader]) => {
                          const hasOTColumn = record.offerTrackerHeaders.includes(otHeader);
                          const hasALColumn = record.avatureListHeaders.includes(alHeader);

                          if (!hasOTColumn && !hasALColumn) return null;

                          const otIndex = record.offerTrackerHeaders.indexOf(otHeader);
                          const otValue = otIndex >= 0 ? record.offerTrackerRow[otIndex] : 'N/A';

                          const hasDifference = record.differences.some(
                            diff => diff.column === otHeader && diff.file1Value !== undefined
                          );

                          return (
                            <TableCell
                              key={otHeader}
                              sx={{
                                backgroundColor: hasDifference ? 'error.light' : 'inherit',
                                fontWeight: hasDifference ? 'bold' : 'normal',
                                border: hasDifference ? '2px solid' : '1px solid',
                                borderColor: hasDifference ? 'error.main' : 'divider',
                                fontFamily: 'monospace',
                              }}
                            >
                              {String(otValue || 'EMPTY')}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Avature List Row */}
                      <TableRow sx={{ backgroundColor: 'info.light' }}>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main' }}>
                          Avature List
                        </TableCell>
                        {Object.entries(COLUMN_MAPPING).map(([otHeader, alHeader]) => {
                          const hasOTColumn = record.offerTrackerHeaders.includes(otHeader);
                          const hasALColumn = record.avatureListHeaders.includes(alHeader);

                          if (!hasOTColumn && !hasALColumn) return null;

                          const alIndex = record.avatureListHeaders.indexOf(alHeader);
                          const alValue = alIndex >= 0 ? record.avatureListRow[alIndex] : 'N/A';

                          const hasDifference = record.differences.some(
                            diff => diff.column === alHeader && diff.file2Value !== undefined
                          );

                          return (
                            <TableCell
                              key={otHeader}
                              sx={{
                                backgroundColor: hasDifference ? 'error.light' : 'inherit',
                                fontWeight: hasDifference ? 'bold' : 'normal',
                                border: hasDifference ? '2px solid' : '1px solid',
                                borderColor: hasDifference ? 'error.main' : 'divider',
                                fontFamily: 'monospace',
                              }}
                            >
                              {String(alValue || 'EMPTY')}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}

          {detailedComparisonData.length === 0 && (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No detailed comparison data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDetailView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}

      {/* Variance Analysis Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Error Alert for Variance */}
          {varianceError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setVarianceError('')}>
              {varianceError}
            </Alert>
          )}

          {/* File Upload Section for Variance */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                  <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Offer Tracker File
                </Typography>
                <FileUploadZone
                  onFileSelect={handleOfferTrackerUpload}
                  label="Upload Offer Tracker Excel File"
                  fileName={offerTrackerFile?.name}
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                  <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Financial Year Data File
                </Typography>
                <FileUploadZone
                  onFileSelect={handleFy26Upload}
                  label="Upload FY26 Data Excel File"
                  fileName={fy26File?.name}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Validate Button */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleVarianceValidation}
              disabled={!offerTrackerFile || !fy26File || varianceLoading}
              startIcon={varianceLoading ? <CircularProgress size={20} color="inherit" /> : <ValidateIcon />}
              sx={{
                px: 4,
                py: 1.5,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'grey.300',
                },
              }}
            >
              {varianceLoading ? 'Processing...' : 'Validate & Process Variance Analysis'}
            </Button>
          </Box>

          {/* Variance Results */}
          {varianceResult && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
                <ValidateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Variance Analysis Results
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main">
                        {varianceResult.processing_summary?.matched_records || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Matched Records
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        {varianceResult.processing_summary?.total_fy26_records || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total FY26 Records
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {varianceResult.processing_summary?.total_offered_across_all || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Offered
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {varianceResult.processing_summary?.total_projected_joins || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Projected Joins
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Download Results Button */}
              {varianceResult.excel_file_url && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleVarianceDownload}
                    disabled={varianceLoading}
                    sx={{ px: 3, py: 1 }}
                  >
                    Download Variance Report
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExcelComparisonPage;
